import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import { useGetOneDataset } from "#src/components/hooks/adapters/useDatasets";
import useLocalization from "#src/hooks/useLocalization";
import { DataSetSubmissionDetailRoutePath } from "#src/routes/data-tools/submissions/datasets/[transactionId]/detail";
import { linkToFacilityDetail } from "#src/routes/organization/facilities/[facilityId]";
import { WorkflowCategoryDetailsRoutePath } from "#src/routes/workflows/categories/[workflowCategoryId]";
import { WorkflowTemplateDetailsRoutePath } from "#src/routes/workflows/templates/[workflowTemplateId]";
import { renderWorkflowDisplayVariable } from "#src/components/Workflows/WorkflowVariableDisplay";
import { linkToAssetDetailPage } from "#src/utils/links";
import { useMultiLookupAssetDetails } from "#src/utils/lookups";
import { KeyValuePanel } from "@validereinc/common-components";
import { AssetType, type WorkflowType } from "@validereinc/domain";
import { toTitleCaseString } from "@validereinc/utilities";
import React, { ReactNode, useMemo } from "react";
import {
  getWorkflowDetailDisplayVariables,
  getWorkflowLookupIdsByEntityType,
} from "./WorkflowDetailKeyValuePanel.helpers";

export const WorkflowDetailKeyValuePanel = ({
  workflowDetails,
}: {
  workflowDetails?: WorkflowType;
}) => {
  const { localize } = useLocalization();
  const datasetId = workflowDetails?.dataset_trigger?.dataset_id ?? "";

  const datasetQuery = useGetOneDataset({ id: datasetId });

  const datasetName = datasetQuery.data?.data?.name
    ? toTitleCaseString(datasetQuery.data.data.name, { intent: "content" })
    : undefined;

  const isDatasetLoading = Boolean(datasetId) && datasetQuery.isLoading;

  const displayVariables = useMemo(
    () => getWorkflowDetailDisplayVariables(workflowDetails),
    [workflowDetails]
  );

  const lookupIdsByEntityType = useMemo(
    () => getWorkflowLookupIdsByEntityType(displayVariables),
    [displayVariables]
  );

  const {
    assetDetailsMap: lookupAssetDetailsById,
    isLoading: isLoadingLookupAssets,
  } = useMultiLookupAssetDetails(lookupIdsByEntityType);

  const data: Array<{ title: string; value: ReactNode }> = useMemo(
    () =>
      workflowDetails
        ? [
            {
              title: "Category",
              value: workflowDetails?.workflow_template?.workflow_category
                ?.name ? (
                <RoutingLink
                  to={WorkflowCategoryDetailsRoutePath.toLinkParts({
                    pathParams: {
                      workflowCategoryId:
                        workflowDetails.workflow_template.workflow_category.id,
                    },
                  })}
                >
                  {workflowDetails.workflow_template.workflow_category.name}
                </RoutingLink>
              ) : (
                "-"
              ),
            },
            {
              title: "Description",
              value: workflowDetails?.description ?? "-",
            },
            {
              title: "Template",
              value: workflowDetails?.workflow_template ? (
                <RoutingLink
                  to={WorkflowTemplateDetailsRoutePath.toLinkParts({
                    pathParams: {
                      workflowTemplateId: workflowDetails.workflow_template_id,
                    },
                  })}
                >
                  {workflowDetails.workflow_template.name}
                </RoutingLink>
              ) : (
                "-"
              ),
            },
            ...(workflowDetails.dataset_trigger?.dataset_id
              ? [
                  {
                    title: "Dataset",
                    value: datasetName ?? "-",
                  },
                  {
                    title: "Dataset Submission",
                    value: workflowDetails.dataset_trigger.transaction_id ? (
                      <RoutingLink
                        to={DataSetSubmissionDetailRoutePath.toLinkParts({
                          pathParams: {
                            transactionId:
                              workflowDetails.dataset_trigger.transaction_id,
                          },
                        })}
                      >
                        {datasetName
                          ? `${datasetName} - ${workflowDetails.dataset_trigger.transaction_id.slice(0, 7)}`
                          : workflowDetails.dataset_trigger.transaction_id}
                      </RoutingLink>
                    ) : (
                      "-"
                    ),
                  },
                ]
              : []),
            {
              title: `Associated ${localize("facility")}`,
              value:
                workflowDetails.asset &&
                workflowDetails.asset.asset_type !== AssetType.FACILITY &&
                workflowDetails.facility ? (
                  <RoutingLink
                    key={workflowDetails.facility.id}
                    to={linkToFacilityDetail(workflowDetails.facility.id)}
                  >
                    {workflowDetails.facility.name}
                  </RoutingLink>
                ) : (
                  "-"
                ),
            },
            {
              title: workflowDetails.asset?.asset_type
                ? localize(workflowDetails.asset.asset_type)
                : "Asset",
              value: workflowDetails.asset ? (
                <RoutingLink
                  key={workflowDetails.asset.id}
                  to={linkToAssetDetailPage(
                    workflowDetails.asset.asset_type,
                    workflowDetails.asset.id
                  )}
                >
                  {workflowDetails.asset.name}
                </RoutingLink>
              ) : (
                "-"
              ),
            },
            ...displayVariables.map((variable) => ({
              title: variable.displayName,
              value: renderWorkflowDisplayVariable(
                variable,
                lookupAssetDetailsById
              ),
            })),
          ]
        : [],
    [
      workflowDetails,
      datasetName,
      localize,
      displayVariables,
      lookupAssetDetailsById,
    ]
  );

  return (
    <KeyValuePanel
      panelMaxColumnCount={2}
      panelKeyValueListProps={{ maxRowCount: Math.max(data.length, 1) }}
      displayActionRowCondition="never"
      panelProps={{
        title: "Workflow Details",
        isFluidY: true,
        loaded:
          Boolean(workflowDetails) &&
          !isDatasetLoading &&
          !isLoadingLookupAssets,
      }}
      data={data}
    />
  );
};
